import { isProductConfigurable } from '@/util/isProductConfigurable';

// 判断 Map 中是否所有的 value 都不是 undefined
// const allValuesDefined = [...optionSelections.values()].every(value => value !== undefined);

// 计算 Map 中不是 undefined 的 value 的数量
// const nonUndefinedValuesCountLargeThanThree = [...optionSelections.values()].filter(value => value !== undefined).length >= 3;

// 所有属性都被选中了
// const isAllOptionHasBennSelected = nonUndefinedValuesCountLargeThanThree || allValuesDefined

function findVariant(optionSelections, variants) {
  if (
    !(optionSelections instanceof Map) ||
    !Array.isArray(variants) ||
    optionSelections.size === 0 ||
    variants.length === 0
  ) {
    return null;
  }

  for (const variant of variants) {
    let match = true;

    for (const [optionKey, optionValue] of optionSelections) {
      const attribute = optionSelections.get(optionKey);

      if (typeof attribute === 'undefined') {
        match = false;
        break;
      }

      const foundAttribute = variant.attributes.find(attr => attr.value_index === attribute);

      if (!foundAttribute || foundAttribute.value_index !== attribute) {
        match = false;
        break;
      }
    }

    if (match) {
      return variant;
    }
  }

  return null;
}

// 详情页
// 1. amp (只需要选中颜色选项价格即刻发生改变)
//     没有选中选项
//         显示价: 外层 price_range 里面的 min 里面的 final
//         划线价: 外层 price_range 里面的 max 里面的 regular_price
//     选中颜色选项
//         显示价:
//             1. 有 special 取外层 special + 所有选中属性的price
//             2. 没有 special 取外层 min 里面的 regular + 所有选中属性的price
//         划线价: 外层 price_range 里面的 max 里面的 regular_price
// 2. cp (需要选中所有选项价格发生改变)
//      没有选中选项
//         显示价: 外层 price_range 里面的 min 里面的 final
//         划线价: 外层 price_range 里面的 max 里面的 regular
//     全部选中选项
//         显示价: variant 里面的  min 里面的 final
//         划线价: variant 里面的  max 里面的 regular

export const useGetFinalPriceAndCrossPrice = ({ product, optionSelections }) => {
  // 获的外层的 price_range start
  const { price_range, variants } = product || {};

  const { maximum_price, minimum_price } = price_range || {};

  const { final_price: minimum_price_final_price } = minimum_price || {};
  const { value: minimum_price_final_price_value } = minimum_price_final_price || {};

  const { final_price: maximum_price_final_price } = maximum_price || {};
  const { value: maximum_price_final_price_value, currency } = maximum_price_final_price || {};

  const { regular_price: maximum_price_regular_price } = maximum_price || {};
  const { value: maximum_price_regular_price_value } = maximum_price_regular_price || {};
  // 获的外层的 price_range end

  const variantsResult = findVariant(optionSelections, variants);

  // cp
  if (isProductConfigurable(product)) {
    const allValuesDefined = [...optionSelections.values()].every(value => value !== undefined);

    // 选项没有被全部选中 显示外部的 price_range
    if (!allValuesDefined) {
      return {
        finalPrice: minimum_price_final_price_value,
        crossPrice: maximum_price_regular_price_value,
        hasCrossPrice: minimum_price_final_price_value < maximum_price_regular_price_value,
        currency: currency || 'USD',
      };
    }

    // 没有找到 variant，使用外层的价格
    if (!variantsResult) {
      return {
        finalPrice: minimum_price_final_price_value,
        crossPrice: maximum_price_regular_price_value,
        hasCrossPrice: minimum_price_final_price_value < maximum_price_regular_price_value,
        currency: currency || 'USD',
      };
    }

    const { product: cpProduct } = variantsResult || {};

    const { price_range: cpPriceRange } = cpProduct || {};

    const { maximum_price: cp_maximum_price, minimum_price: cp_minimum_price } = cpPriceRange || {};

    const { final_price: cp_maximum_price_final_price } = cp_maximum_price || {};
    const { currency: cp_currency } = cp_maximum_price_final_price || {};

    const { final_price: cp_minimum_price_final_price } = cp_minimum_price || {};
    const { value: cp_minimum_price_final_price_value } = cp_minimum_price_final_price || {};
    const cp_maximum_price_regular_price_value = cp_maximum_price?.regular_price?.value;

    return {
      finalPrice: cp_minimum_price_final_price_value,
      crossPrice: cp_maximum_price_regular_price_value,
      hasCrossPrice: cp_minimum_price_final_price_value < cp_maximum_price_regular_price_value,
      currency: cp_currency || 'USD',
    };
  }

  // 没选中任何选项时
  // 显示价: 外层 price_range 里面的 min 里面的 final
  // 划线价: 外层 price_range 里面的 max 里面的 regular_price
  const optionSelectionsArr = Array.from(optionSelections || []);

  if (
    !optionSelectionsArr.length ||
    optionSelectionsArr.every(([, value_index]) => value_index === undefined || value_index === null)
  ) {
    return {
      finalPrice: minimum_price_final_price_value,
      crossPrice: maximum_price_final_price_value,
      hasCrossPrice: minimum_price_final_price_value < maximum_price_final_price_value,
      currency: currency || 'USD',
    };
  }

  const ampProtOptions = product?.ampProtOption?.items || [];
  const priceAffect = optionSelectionsArr.reduce((prev, [attribute_id, value_index]) => {
    if (value_index === undefined || value_index === null) {
      return prev;
    }

    const price =
      ampProtOptions.find(x => x.attribute_id === attribute_id)?.values?.find(x => x.value_index === value_index)
        ?.price || 0;

    return prev + price;
  }, 0);

  // 下面是 amp 的 price 逻辑 (所有选项被选中)
  const { special_price } = product || {};

  let finalPrice;

  if (special_price) {
    finalPrice = special_price + priceAffect;
  } else {
    finalPrice = maximum_price_regular_price_value + priceAffect;
  }

  return {
    finalPrice: finalPrice,
    crossPrice: maximum_price_regular_price_value,
    hasCrossPrice: finalPrice < maximum_price_regular_price_value,
    currency: currency || 'USD',
  };
};
